import { FC, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import Footer from 'src/components/Footer';
import { brand } from 'src/theme/brand';

interface AppPageProps {
  title: string;
  heading?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  withFooter?: boolean;
}

/**
 * Consistent page chrome for list/detail screens across the admin app.
 */
const AppPage: FC<AppPageProps> = ({
  title,
  heading,
  description,
  actions,
  children,
  maxWidth = false,
  withFooter = true
}) => {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container
        maxWidth={maxWidth}
        sx={{
          px: { xs: 0, sm: 0 },
          pb: { xs: 1.5, sm: 2 },
          flex: 1
        }}
      >
        {(heading || description || actions) && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Box minWidth={0}>
              {heading && (
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 18, sm: 20 },
                    lineHeight: 1.25,
                    mb: description ? 0.25 : 0,
                    color: brand.ink
                  }}
                >
                  {heading}
                </Typography>
              )}
              {description && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.text.secondary,
                    maxWidth: 760,
                    lineHeight: 1.45
                  }}
                >
                  {description}
                </Typography>
              )}
            </Box>
            {actions && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: { xs: 'stretch', sm: 'flex-end' },
                  '& > *': {
                    [theme.breakpoints.down('sm')]: {
                      width: '100%'
                    }
                  }
                }}
              >
                {actions}
              </Box>
            )}
          </Stack>
        )}
        {children}
      </Container>
      {withFooter && <Footer />}
    </>
  );
};

export default AppPage;
